import { SensorTarget, SensorType } from './Sensor';
export var SentinelType;
(function (SentinelType) {
    SentinelType["NONE"] = "";
    SentinelType["TEMPERATURE"] = "BasicTemperatureThreshold";
    SentinelType["HUMIDITY"] = "HumidityThreshold";
    SentinelType["BINARY"] = "BasicDoorSwitch";
    SentinelType["BATTERY_LEVEL_CHECK"] = "BatteryLevelCheck";
    SentinelType["CONNECTIVITY"] = "OnlineStatusCheck";
    SentinelType["TEMPERATURE_AND_DOOR"] = "TemperatureAndDoorCheck";
    SentinelType["BINARY_COUNTER"] = "DoorOpenCounter";
    SentinelType["CUSTOMER_COMFORT"] = "CustomerComfortCheck";
    SentinelType["ACTIVE_POWER_THRESHOLD"] = "ActivePowerThreshold";
    SentinelType["GATEWAY_ONLINE_STATUS"] = "GatewayOnlineStatus";
    SentinelType["ENERGYPRO_ONLINE_STATUS"] = "EnergyProOnlineStatus";
    SentinelType["DOOR_SIREN_CHECK"] = "DoorSirenCheck";
    SentinelType["CONTROLS_HEALTH_CHECK"] = "ControlsHealthCheck";
    // TODO: delete LOCAL_OVERRIDE_CHECK later on
    SentinelType["LOCAL_OVERRIDE_CHECK"] = "LocalOverrideCheck";
    SentinelType["SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK"] = "LocalOverrideCheck";
    SentinelType["THERMOSTAT_LOCAL_OVERRIDE_CHECK"] = "ThermostatLocalOverrideCheck";
})(SentinelType || (SentinelType = {}));
export var SentinelParameterKey;
(function (SentinelParameterKey) {
    SentinelParameterKey["rangeLowerBound"] = "Range Lower Bound";
    SentinelParameterKey["rangeUpperBound"] = "Range Upper Bound";
    SentinelParameterKey["threshold"] = "Threshold";
    SentinelParameterKey["openClosed"] = "Door Access";
})(SentinelParameterKey || (SentinelParameterKey = {}));
export const SensorTypeToSentinelType = {
    TEMPERATURE: [SentinelType.TEMPERATURE, SentinelType.TEMPERATURE_AND_DOOR],
    HUMIDITY: [SentinelType.HUMIDITY],
    BINARY: [SentinelType.BINARY],
};
export const sentinelConfigurations = {
    [SentinelType.TEMPERATURE]: {
        type: SentinelType.TEMPERATURE,
        title: 'Temperature',
        description: 'Monitor the temperature of your equipment and spaces',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.HUMIDITY]: {
        type: SentinelType.HUMIDITY,
        title: 'Humidity',
        description: 'Monitor the humidity of your spaces',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.ACTIVE_POWER_THRESHOLD]: {
        type: SentinelType.ACTIVE_POWER_THRESHOLD,
        title: 'Active Power',
        description: 'Monitor the active power of your equipment',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.BINARY]: {
        type: SentinelType.BINARY,
        title: 'Door',
        description: 'Monitor the doors of your equipment and spaces',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.CUSTOMER_COMFORT]: {
        type: SentinelType.CUSTOMER_COMFORT,
        title: 'Customer Comfort',
        description: 'Monitor ambient temperature and humidity to optimize indoor temperature',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.TEMPERATURE_AND_DOOR]: {
        type: SentinelType.TEMPERATURE_AND_DOOR,
        title: 'Temperature & Door',
        description: 'Monitor the doors and temperature of your spaces',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.DOOR_SIREN_CHECK]: {
        type: SentinelType.DOOR_SIREN_CHECK,
        title: 'Door & Siren',
        description: 'Monitor the doors of your equipment and spaces and include the siren function of the hub',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.BINARY_COUNTER]: {
        type: SentinelType.BINARY_COUNTER,
        title: 'Door Counter',
        description: 'Counts open and closes for a 24 hour period',
        customSchedule: false,
        featureFlag: false,
        customDelay: false,
    },
    [SentinelType.SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK]: {
        type: SentinelType.SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK,
        title: 'SiteController Override',
        description: 'Monitors when local override on SiteController is switched "ON"',
        customSchedule: false,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK]: {
        type: SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK,
        title: 'Thermostat Override',
        description: 'Monitors when local settings have breached the original configuration',
        customSchedule: false,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.BATTERY_LEVEL_CHECK]: {
        type: SentinelType.BATTERY_LEVEL_CHECK,
        title: 'Battery Level Check',
        description: 'Battery status check for wireless sensors',
        customSchedule: false,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.CONNECTIVITY]: {
        type: SentinelType.CONNECTIVITY,
        title: 'Connectivity',
        description: 'Online status check for devices',
        customSchedule: false,
        featureFlag: false,
        customDelay: true,
    },
    [SentinelType.CONTROLS_HEALTH_CHECK]: {
        type: SentinelType.CONTROLS_HEALTH_CHECK,
        title: 'Thermostat Health',
        description: 'Monitor online status of 3rd party Thermostat Sensors',
        customSchedule: true,
        featureFlag: false,
        customDelay: true,
    },
};
export const featureHiddenSentinels = Object.values(sentinelConfigurations)
    .filter((s) => s.featureFlag)
    .map((s) => s.type);
export const customDelaySentinels = Object.values(sentinelConfigurations)
    .filter((s) => s.customDelay)
    .map((s) => s.type);
export const sentinelTypesList = Object.values(sentinelConfigurations);
// export const OPEN / CLOSED
export var SentinelTargetType;
(function (SentinelTargetType) {
    SentinelTargetType["SENSOR"] = "SENSOR";
    SentinelTargetType["EQUIPMENT"] = "EQUIPMENT";
    SentinelTargetType["SPACE"] = "SPACE";
    SentinelTargetType["DOOR"] = "DOOR";
    SentinelTargetType["GATEWAY"] = "GATEWAY";
    SentinelTargetType["ENERGYPRO"] = "ENERGYPRO";
    SentinelTargetType["EDGEAPP"] = "EDGEAPP";
})(SentinelTargetType || (SentinelTargetType = {}));
export var ComparisonType;
(function (ComparisonType) {
    ComparisonType["default"] = "";
    ComparisonType["eq"] = "eq";
    ComparisonType["gt"] = "gt";
    ComparisonType["lt"] = "lt";
    ComparisonType["inner"] = "inner";
    ComparisonType["outer"] = "outer";
})(ComparisonType || (ComparisonType = {}));
export const sentinelComparisonDefaults = {
    [SentinelType.TEMPERATURE]: {
        fixed: {
            rangeLowerBound: undefined,
            rangeUpperBound: undefined,
            threshold: 8,
        },
        range: {
            rangeLowerBound: 8,
            rangeUpperBound: 32,
            threshold: undefined,
        },
    },
    [SentinelType.ACTIVE_POWER_THRESHOLD]: {
        fixed: {
            rangeLowerBound: undefined,
            rangeUpperBound: undefined,
            threshold: 250,
        },
        range: {
            rangeLowerBound: 200,
            rangeUpperBound: 300,
            threshold: undefined,
        },
    },
    [SentinelType.HUMIDITY]: {
        fixed: {
            rangeLowerBound: undefined,
            rangeUpperBound: undefined,
            threshold: 40,
        },
        range: {
            rangeLowerBound: 40,
            rangeUpperBound: 70,
            threshold: undefined,
        },
    },
};
export const comparisonTypesToSymbol = {
    default: '',
    [ComparisonType.eq]: '=',
    [ComparisonType.gt]: '>',
    [ComparisonType.lt]: '<',
    rg: '-',
    [ComparisonType.inner]: '-',
    [ComparisonType.outer]: '-',
};
export var IntervalUnit;
(function (IntervalUnit) {
    IntervalUnit["DEVICEINTERVAL"] = "DEVICEINTERVAL";
    IntervalUnit["MILLISECOND"] = "MILLISECOND";
})(IntervalUnit || (IntervalUnit = {}));
export var ParametersSwitchOpenClosed;
(function (ParametersSwitchOpenClosed) {
    ParametersSwitchOpenClosed["OPEN"] = "OPEN";
    ParametersSwitchOpenClosed["CLOSED"] = "CLOSED";
})(ParametersSwitchOpenClosed || (ParametersSwitchOpenClosed = {}));
export var ParametersSwitchOnOff;
(function (ParametersSwitchOnOff) {
    ParametersSwitchOnOff["ON"] = "ON";
    ParametersSwitchOnOff["OFF"] = "OFF";
})(ParametersSwitchOnOff || (ParametersSwitchOnOff = {}));
export const defaultTemperatureParams = {
    comparisonType: ComparisonType.eq,
    threshold: 0,
};
export const defaultActivePowerParams = {
    comparisonType: ComparisonType.eq,
    threshold: 0,
};
export const defaultDoorParameters = {
    openClosed: ParametersSwitchOpenClosed.OPEN,
};
export const defaultCustomerComfortParameters = {
    metabolicRate: 1.1,
};
export const defaultBinaryCounterParameters = {
    maxOpeningCount: 1,
};
export const defaultHumidityParams = {
    comparisonType: ComparisonType.eq,
    threshold: 0,
};
export const defaultLocalOverrideParams = {
    delay: {
        unit: IntervalUnit.MILLISECOND,
        value: 20 * 60 * 1000, //default is 20min per bryan c
    },
};
export var SentinelRecipientsChannel;
(function (SentinelRecipientsChannel) {
    SentinelRecipientsChannel["USER_EMAIL"] = "USER_EMAIL";
    SentinelRecipientsChannel["USER_GROUP_EMAIL"] = "USER_GROUP_EMAIL";
})(SentinelRecipientsChannel || (SentinelRecipientsChannel = {}));
export var SentinelPriorityLevel;
(function (SentinelPriorityLevel) {
    SentinelPriorityLevel["NORMAL"] = "NORMAL";
    SentinelPriorityLevel["LOW"] = "LOW";
    SentinelPriorityLevel["MEDIUM"] = "MEDIUM";
    SentinelPriorityLevel["HIGH"] = "HIGH";
    SentinelPriorityLevel["HIGHEST"] = "HIGHEST";
    SentinelPriorityLevel["CRITICAL"] = "CRITICAL";
})(SentinelPriorityLevel || (SentinelPriorityLevel = {}));
export var SentinelRecurType;
(function (SentinelRecurType) {
    SentinelRecurType["NONE"] = "NONE";
    SentinelRecurType["UNTIL_RESOLVED"] = "UNTIL_RESOLVED";
})(SentinelRecurType || (SentinelRecurType = {}));
export var SentinelScheduleWeekDay;
(function (SentinelScheduleWeekDay) {
    SentinelScheduleWeekDay["MONDAY"] = "MONDAY";
    SentinelScheduleWeekDay["TUESDAY"] = "TUESDAY";
    SentinelScheduleWeekDay["WEDNESDAY"] = "WEDNESDAY";
    SentinelScheduleWeekDay["THURSDAY"] = "THURSDAY";
    SentinelScheduleWeekDay["FRIDAY"] = "FRIDAY";
    SentinelScheduleWeekDay["SATURDAY"] = "SATURDAY";
    SentinelScheduleWeekDay["SUNDAY"] = "SUNDAY";
})(SentinelScheduleWeekDay || (SentinelScheduleWeekDay = {}));
export var SentinelScheduleType;
(function (SentinelScheduleType) {
    SentinelScheduleType["DAILY"] = "DAILY";
    SentinelScheduleType["WEEKLY"] = "WEEKLY";
})(SentinelScheduleType || (SentinelScheduleType = {}));
export const sentinelTypeToSensorType = (type) => {
    switch (type) {
        case SentinelType.DOOR_SIREN_CHECK:
        case SentinelType.BINARY:
        case SentinelType.BINARY_COUNTER:
            return [SensorType.BINARY];
        case SentinelType.TEMPERATURE:
            return [SensorType.TEMPERATURE];
        case SentinelType.HUMIDITY:
            return [SensorType.HUMIDITY];
        case SentinelType.TEMPERATURE_AND_DOOR:
            return [SensorType.BINARY, SensorType.TEMPERATURE];
        case SentinelType.CONNECTIVITY:
        case SentinelType.BATTERY_LEVEL_CHECK:
            return [SensorType.BINARY, SensorType.TEMPERATURE, SensorType.HUMIDITY];
        case SentinelType.CUSTOMER_COMFORT:
            return [SensorType.TEMPERATURE, SensorType.HUMIDITY];
        case SentinelType.ACTIVE_POWER_THRESHOLD:
            // return [SensorType.ENERGY];
            return [];
        default:
            return [];
    }
};
export const sentinelTypeToSensorTargetType = (type) => {
    switch (type) {
        case SentinelType.ACTIVE_POWER_THRESHOLD:
        case SentinelType.BINARY_COUNTER:
            return [SensorTarget.EQUIPMENT];
        case SentinelType.DOOR_SIREN_CHECK:
        case SentinelType.TEMPERATURE:
        case SentinelType.HUMIDITY:
        case SentinelType.BINARY:
        case SentinelType.CONNECTIVITY:
        case SentinelType.BATTERY_LEVEL_CHECK:
        case SentinelType.TEMPERATURE_AND_DOOR:
        case SentinelType.CUSTOMER_COMFORT:
            return [SensorTarget.EQUIPMENT, SensorTarget.SPACE];
        case SentinelType.SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK:
        case SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK:
        default:
            return [];
    }
};
export const sentinelTypeToSentinelTargetType = (type) => {
    switch (type) {
        case SentinelType.DOOR_SIREN_CHECK:
        case SentinelType.BINARY:
        case SentinelType.BINARY_COUNTER:
        case SentinelType.TEMPERATURE:
        case SentinelType.HUMIDITY:
            return [SentinelTargetType.EQUIPMENT, SentinelTargetType.SPACE];
        case SentinelType.CONNECTIVITY:
            return [
                SentinelTargetType.SENSOR,
                SentinelTargetType.GATEWAY,
                SentinelTargetType.ENERGYPRO,
                SentinelTargetType.EDGEAPP,
            ];
        case SentinelType.CONTROLS_HEALTH_CHECK:
            return [SentinelTargetType.EDGEAPP];
        case SentinelType.BATTERY_LEVEL_CHECK:
            return [SentinelTargetType.SENSOR];
        case SentinelType.TEMPERATURE_AND_DOOR:
            return [
                SentinelTargetType.SPACE,
                SentinelTargetType.EQUIPMENT,
                SentinelTargetType.SENSOR,
            ];
        case SentinelType.CUSTOMER_COMFORT:
            return [SentinelTargetType.SENSOR];
        case SentinelType.THERMOSTAT_LOCAL_OVERRIDE_CHECK:
        case SentinelType.ACTIVE_POWER_THRESHOLD:
            return [SentinelTargetType.EQUIPMENT];
        case SentinelType.SITE_CONTROLLER_LOCAL_OVERRIDE_CHECK:
            return [SentinelTargetType.GATEWAY];
        default:
            return [];
    }
};
export const initialSentinelDelay = {
    unit: IntervalUnit.MILLISECOND,
    value: 0,
};
export const initialSentinelEscalation = {
    ix: 0,
    recipients: [],
    name: '',
    message: '',
    priorityLevel: SentinelPriorityLevel.NORMAL,
    recurType: SentinelRecurType.NONE,
    // The first sentinel escalation should always be single run true according to Marcus
    singleRun: true,
    singleRunDelay: {
        unit: IntervalUnit.MILLISECOND,
        value: 0,
    },
    recurrenceLimit: null,
    recurrenceLimitChecked: false,
    channelType: [],
};
export const initialSentinelEscalationWithoutDelay = {
    ix: 0,
    recipients: [],
    name: '',
    message: '',
    priorityLevel: SentinelPriorityLevel.NORMAL,
    recurType: SentinelRecurType.NONE,
    singleRun: false,
    singleRunDelay: {
        unit: IntervalUnit.MILLISECOND,
        value: 0,
    },
    recurrenceLimit: null,
    recurrenceLimitChecked: false,
    channelType: [],
};
export const initialSentinelTimeRange = {
    begin: '06:00',
    end: '18:00',
};
export const initialSentinelSchedule = {
    scheduleType: SentinelScheduleType.DAILY,
    weekDays: [],
    timeRanges: [initialSentinelTimeRange],
};
export const SentinelTypeToThresholdLineType = {
    [SentinelType.TEMPERATURE_AND_DOOR]: 'TEMPERATURE',
    [SentinelType.TEMPERATURE]: 'TEMPERATURE',
    [SentinelType.HUMIDITY]: 'HUMIDITY',
    [SentinelType.BINARY]: 'BINARY',
    [SentinelType.ACTIVE_POWER_THRESHOLD]: 'ENERGY',
};
export const SentinelTypeToLabel = {
    BasicTemperatureThreshold: 'Temperature',
    HumidityThreshold: 'Humidity',
    BasicDoorSwitch: 'Door',
    BatteryLevelCheck: 'Battery Level Check',
    OnlineStatusCheck: 'Sensor Connectivity',
    GatewayOnlineStatus: 'Hub Connectivity',
    EnergyProOnlineStatus: 'EnergyPro Connectivity',
    DoorOpenCounter: 'Door Counter',
    TemperatureAndDoorCheck: 'Temperature & Door',
    CustomerComfortCheck: 'Customer Comfort',
    ActivePowerThreshold: 'Active Power',
    DoorSirenCheck: 'Door Siren',
    ControlsHealthCheck: 'Controls Health',
    LocalOverrideCheck: 'Local Override',
    ThermostatLocalOverrideCheck: 'ThermostatLocalOverrideCheck'
};
export const SentinelTypeToSensorType = {
    [SentinelType.TEMPERATURE]: SensorType.TEMPERATURE,
    [SentinelType.HUMIDITY]: SensorType.HUMIDITY,
    [SentinelType.BINARY]: SensorType.BINARY,
    [SentinelType.ACTIVE_POWER_THRESHOLD]: 'ENERGY',
};
export var TargetType;
(function (TargetType) {
    TargetType["SENSOR"] = "SENSOR";
    TargetType["EQUIPMENT"] = "EQUIPMENT";
    TargetType["SPACE"] = "SPACE";
    TargetType["DOOR"] = "DOOR";
    TargetType["GATEWAY"] = "GATEWAY";
})(TargetType || (TargetType = {}));
export const defaultBatteryCheckParameters = {};
export const defaultControlsHealthCheckParameters = {};
export var SentinelChannelType;
(function (SentinelChannelType) {
    SentinelChannelType["EMAIL"] = "EMAIL";
    SentinelChannelType["SIMPLYCHECK"] = "SIMPLYCHECK";
    SentinelChannelType["KIOSK"] = "KIOSK";
})(SentinelChannelType || (SentinelChannelType = {}));
